import {
  BarChartSkeleton,
  chartColors,
  DateFormatted,
  ExtendedStatCard,
  LoadingSprints,
  SearchTable,
  Table,
  TableItem,
  TableRow,
  Tabs,
  Tag,
  TextHeader,
  UsersHealthSidebar,
} from '@components';
import StatBarchartCard from '@components/cards/StatBarchartCard';
import { PeerHealthCard } from '@components/cards/StatDonutChart';
import TableWrapper from '@components/tables/TableWrapper';
import Icon from '@components/typography/AppIcon';
import { useAside } from '@hooks';
import {
  approvePeerVpn,
  fetchVpnPeers,
} from '@stores/netbird/NetbirdPeersSlice';
import { ListComponent } from '@utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCompany } from '../../../hooks/useCompany';
import { AuditEvents, NetbirdUser } from '../../../interfaces/Netbird';
import {
  approveVpnUser,
  fetchVpnUsers,
} from '../../../state/netbird/NetbirdUsersSlice';

import { fetchVpnEvents } from '@stores/netbird/NetbirdEventsSlice';
import { AppDispatch, RootState } from '../../../state/store';

export const getPeerStatus = (peer: any) => {
  if (peer.connected) {
    return { color: 'bg-green-500', animate: true };
  }

  if (peer.login_expired) {
    return { color: 'bg-orange-500', animate: false };
  }

  return { color: 'bg-zinc-400', animate: false };
};

export const ZeroTrustContainer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useCompany();
  const { openAside } = useAside();

  // Selectors for users
  const vpnUsers = useSelector((state: RootState) => state.netbirdUsers.users);
  const vpnUsersStatus = useSelector(
    (state: RootState) => state.netbirdUsers.vpnUsersStatus
  );
  const usersLoading = useSelector(
    (state: RootState) => state.netbirdUsers.status === 'loading'
  );

  // Selectors for peers
  const vpnPeers = useSelector((state: RootState) => state.netbirdPeers.peers);
  const vpnPeersStatus = useSelector(
    (state: RootState) => state.netbirdPeers.vpnPeersStatus
  );
  const vpnOsPeers = useSelector(
    (state: RootState) => state.netbirdPeers.vpnOsPeers
  );
  const vpnPeersCount = useSelector(
    (state: RootState) => state.netbirdPeers.count
  );
  const peersLoading = useSelector(
    (state: RootState) => state.netbirdPeers.status === 'loading'
  );

  // Selectors for events
  const vpnEventsData = useSelector(
    (state: RootState) => state.netbirdEvents.events
  );
  const eventsLoading = useSelector(
    (state: RootState) => state.netbirdEvents.status === 'loading'
  );

  type UserFilter = 'all' | 'pending' | 'blocked' | 'inactive' | 'active';
  const [userFilter, setUserFilter] = useState<UserFilter>('all');

  const tableHeaders = ['', 'Gebruiker', 'Status', 'Laatste login', 'Role'];

  const peerTableHeaders = [
    '',
    'Naam',
    'IP',
    'Platform',
    'Last seen',
    'Status',
    'Groups',
  ];

  const useInEvents = [
    'User logged in peer',
    'Peer login expired',
    'User blocked',
    'User unblocked',
    'User approved',
    'Peer login expired',
  ];

  // Fetch data on mount and when companyId changes
  useEffect(() => {
    if (companyId) {
      dispatch(fetchVpnUsers());
      dispatch(fetchVpnPeers(companyId));
      dispatch(fetchVpnEvents({ companyId, eventType: 'audit' }));
    }
  }, [companyId, dispatch]);

  const peerHealthConfig = [
    { key: 'true', label: 'Online', color: chartColors[1] },
    { key: 'false', label: 'Offline', color: chartColors[11] },
    { key: 'approvalPending', label: 'Pending', color: chartColors[2] },
    { key: 'loginExpired', label: 'Expired', color: chartColors[3] },
  ];

  const userHealthConfig = [
    { key: 'active', label: 'Online', color: chartColors[1] },
    { key: 'approvalPending', label: 'Pending', color: chartColors[8] },
    { key: 'blocked', label: 'Geblokkeerd', color: chartColors[12] },
  ];

  // Filter events (real-time from Redux)
  const vpnEvents = useMemo(() => {
    return vpnEventsData.filter((item: AuditEvents) =>
      useInEvents.includes(item.activity)
    );
  }, [vpnEventsData]);

  // const handleClickPeerHealth = () => {
  //   openAside({
  //     component: 'vpnPeersContainer',
  //     data: {
  //       peers: vpnPeers,
  //       count: vpnPeersCount,
  //       status: vpnPeersStatus,
  //     },
  //     title: 'VPN Peers',
  //   });
  // };

  const handleClickUser = (user: NetbirdUser) => {
    openAside({
      component: 'vpnUserDetailContainer',
      data: {
        userId: user.id,
      },
      title: 'VPN Gebruiker',
    });
  };

  const isNoLoginDate = (date?: string | null) => {
    return !date || date.startsWith('0001-01-01');
  };

  const handleFilter = (filter: UserFilter) => {
    setUserFilter((current) => (current === filter ? 'all' : filter));
  };

  const filteredVpnUsers = useMemo(() => {
    if (!vpnUsers) return [];

    switch (userFilter) {
      case 'pending':
        return vpnUsers.filter((user) => user.pending_approval);

      case 'blocked':
        return vpnUsers.filter((user) => user.status === 'blocked');

      case 'inactive':
        return vpnUsers.filter((user) => isNoLoginDate(user.last_login));

      case 'active':
        return vpnUsers.filter(
          (user) => user.status === 'active' && !user.pending_approval
        );

      case 'all':
      default:
        return vpnUsers;
    }
  }, [vpnUsers, userFilter]);

  const getVpnStatus = (user: any) => {
    if (user.pending_approval) {
      return {
        color: 'bg-yellow-500',
        textColor: 'text-yellow-500',
        label: 'Pending approval',
        animate: 'animate-pulse',
      };
    }

    if (user.status === 'active') {
      return {
        color: 'bg-green-500',
        textColor: 'text-green-500',
        label: 'CMC Zero Trust enabled',
      };
    }

    return {
      color: 'bg-red-500',
      textColor: 'text-red-500',
      label: 'Blocked',
    };
  };

  // Dispatch approve action - real-time update through Redux
  const approveUser = async (userId: string) => {
    try {
      await dispatch(
        approveVpnUser({
          userId,
          companyId: companyId || 0,
        })
      );
    } catch (err) {
      console.error('Approve failed', err);
    }
  };

  // Dispatch peer approval action - real-time update through Redux
  const approvePeer = async (peerId: string) => {
    try {
      await dispatch(
        approvePeerVpn({
          peerId,
          companyId: companyId || 0,
        })
      );
    } catch (err) {
      console.error('Peer approval failed', err);
    }
  };

  const pendingPeers =
    vpnPeers?.filter((peer) => peer.approval_required).length ?? 0;

  const pendingUsers =
    vpnUsers?.filter((user) => user.pending_approval).length ?? 0;

  const pendingApprovals = pendingPeers + pendingUsers;

  const serverCount = vpnPeers.filter(
    (p) =>
      p.os?.toLowerCase().includes('linux') ||
      p.os?.toLowerCase().includes('server') ||
      p.os?.toLowerCase().includes('ubuntu') ||
      p.hostname?.toLowerCase().includes('srv') ||
      p.hostname?.toLowerCase().includes('server')
  ).length;

  const clientCount = vpnPeers.length - serverCount;

  const adminUsers =
    vpnUsers?.filter(
      (user) =>
        user.role?.toLowerCase() === 'admin' ||
        user.role?.toLowerCase() === 'owner'
    ).length ?? 0;

  const UserTable = () => (
    <div className="grid grid-cols-12 gap-4">
      {/* LEFT: TABLE */}
      <div className="col-span-9 min-w-0">
        <TableWrapper>
          <SearchTable placeholder="Gebruiker zoeken..." value={'search'} />

          <FilterPills
            activeFilter={userFilter}
            onFilter={handleFilter}
            users={vpnUsers}
          />

          <Table header={tableHeaders}>
            <ListComponent
              data={filteredVpnUsers}
              renderItem={(user: NetbirdUser) => (
                <TableRow onClick={() => handleClickUser(user)}>
                  <TableItem className="group overflow-visible">
                    {(() => {
                      const status = getVpnStatus(user);
                      if (!status) return null;

                      return (
                        <div className="relative h-6 w-6">
                          {user.pending_approval && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                approveUser(user.id);
                              }}
                              className="absolute left-0 top-0 z-20 flex h-6 w-6 items-center
                              overflow-hidden whitespace-nowrap rounded-full bg-yellow-100
                              pl-5 pr-0 text-xs text-yellow-700
                              transition-all duration-300 ease-out
                              group-hover:w-28 group-hover:pr-2 group-hover:bg-yellow-200"
                            >
                              <span className="opacity-0 transition-opacity duration-200 delay-100 group-hover:opacity-100">
                                Goedkeuren
                              </span>
                            </button>
                          )}

                          {/* DOT */}
                          <div className="pointer-events-none absolute left-0 top-0 z-10 flex h-6 w-6 items-center justify-center">
                            <span
                              className={`absolute inline-flex h-2.5 w-2.5 rounded-full ${status.color} ${
                                status.animate
                                  ? 'animate-ping opacity-40'
                                  : 'opacity-0'
                              }`}
                            />
                            <span
                              className={`relative inline-flex h-2 w-2 rounded-full ${status.color}`}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </TableItem>

                  <TableItem>
                    <div className="flex flex-col">
                      <span className="font-medium">{user.name}</span>
                      <span className="text-xs text-zinc-500">
                        {user.email}
                      </span>
                    </div>
                  </TableItem>

                  <TableItem>
                    <Tag
                      label={user.pending_approval ? 'pending' : user.status}
                      style={user.pending_approval ? 'pending' : user.status}
                      lowercase
                    />
                  </TableItem>

                  <TableItem>
                    <DateFormatted date={user.last_login} noDate="-" />
                  </TableItem>

                  <TableItem>
                    <Tag label={user.role} style={user.role} lowercase />
                  </TableItem>
                </TableRow>
              )}
            />
          </Table>
        </TableWrapper>
      </div>

      {/* RIGHT: SIDEBAR */}
      <div className="col-span-3 min-w-0">
        <div className="sticky top-6 space-y-4">
          {vpnUsersStatus && <UsersHealthSidebar data={vpnUsersStatus} />}

          {vpnUsers && <UsersRecentActivity users={vpnUsers} />}
        </div>
      </div>
    </div>
  );

  const PeerTable = () => {
    const handleClickPeer = (peer: any) => {
      openAside({
        component: 'vpnPeerContainer',
        data: {
          peer: peer,
        },
        title: 'VPN Peer',
      });
    };

    return (
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-9 min-w-0">
          <TableWrapper>
            <SearchTable placeholder="Peer zoeken..." value={'search'} />

            <Table header={peerTableHeaders}>
              <ListComponent
                data={vpnPeers}
                renderItem={(peer: any) => {
                  const status = getPeerStatus(peer);

                  return (
                    <TableRow onClick={() => handleClickPeer(peer)}>
                      {/* STATUS DOT */}
                      <TableItem className="group overflow-visible">
                        <div className="relative h-6 w-6">
                          {peer.approval_required && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                approvePeer(peer.id);
                              }}
                              className="absolute left-0 top-0 z-20 flex h-6 w-6 items-center
                              overflow-hidden whitespace-nowrap rounded-full bg-yellow-100
                              pl-5 pr-0 text-xs text-yellow-700
                              transition-all duration-300 ease-out
                              group-hover:w-28 group-hover:pr-2 group-hover:bg-yellow-200"
                            >
                              <span className="opacity-0 transition-opacity duration-200 delay-100 group-hover:opacity-100">
                                Goedkeuren
                              </span>
                            </button>
                          )}

                          <div className="pointer-events-none absolute left-0 top-0 z-10 flex h-6 w-6 items-center justify-center">
                            <span
                              className={`absolute inline-flex h-2.5 w-2.5 rounded-full ${status.color} ${
                                status.animate
                                  ? 'animate-ping opacity-40'
                                  : 'opacity-0'
                              }`}
                            />
                            <span
                              className={`relative inline-flex h-2 w-2 rounded-full ${status.color}`}
                            />
                          </div>
                        </div>
                      </TableItem>

                      {/* NAME */}
                      <TableItem>
                        <div className="flex flex-col">
                          <span className="font-medium">{peer.hostname}</span>
                          <span className="text-xs text-zinc-500">
                            {peer.dns_label}
                          </span>
                        </div>
                      </TableItem>

                      {/* IP */}
                      <TableItem>
                        <div className="flex flex-col text-xs">
                          <span>{peer.ip}</span>
                          <span className="text-zinc-400">
                            {peer.connection_ip}
                          </span>
                        </div>
                      </TableItem>

                      {/* PLATFORM */}
                      <TableItem>
                        <div className="flex flex-col text-xs">
                          <span>{peer.os}</span>
                          <span className="text-zinc-400">v{peer.version}</span>
                        </div>
                      </TableItem>

                      {/* LAST SEEN */}
                      <TableItem>
                        <DateFormatted date={peer.last_seen} noDate="-" />
                      </TableItem>

                      {/* STATUS */}
                      <TableItem>
                        <Tag
                          label={
                            peer.connected
                              ? 'online'
                              : peer.login_expired
                                ? 'expired'
                                : 'offline'
                          }
                          style={
                            peer.connected
                              ? 'success'
                              : peer.login_expired
                                ? 'danger'
                                : 'neutral'
                          }
                          lowercase
                        />
                      </TableItem>

                      {/* GROUPS */}
                      <TableItem>
                        <div className="flex flex-wrap gap-1">
                          {peer.groups
                            ?.slice(0, 2)
                            .map((g: any) => (
                              <Tag
                                key={g.id}
                                label={g.name}
                                style="info"
                                lowercase
                              />
                            ))}
                          {peer.groups?.length > 2 && (
                            <span className="text-xs text-zinc-400">
                              +{peer.groups.length - 2}
                            </span>
                          )}
                        </div>
                      </TableItem>
                    </TableRow>
                  );
                }}
              />
            </Table>
          </TableWrapper>
        </div>
        <div className="col-span-3 min-w-0">
          <div className="sticky top-6 space-y-4">
            {vpnPeersStatus && (
              <PeerHealthCard
                title="Peer Health"
                subtitle="Connection status breakdown"
                icon={<Icon name="Bell" size="sm" />}
                data={vpnPeersStatus}
                config={peerHealthConfig}
                total={vpnPeers.length}
              />
            )}

            {vpnOsPeers ? (
              <StatBarchartCard
                title="Peer distribution"
                subtitle="Verdeling van Peers & client devices"
                icon={<Icon name="Monitor" size="md" />}
                data={vpnOsPeers}
              />
            ) : (
              <BarChartSkeleton />
            )}
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    {
      nav: 'Gebruikers',
      component: <UserTable />,
    },
    {
      nav: 'Peers',
      component: <PeerTable />,
    },
  ];

  const UsersRecentActivity = ({ users }: { users: NetbirdUsers }) => {
    const lastLogins = users
      .filter((u) => u.last_login)
      .sort(
        (a, b) =>
          new Date(b.last_login).getTime() - new Date(a.last_login).getTime()
      )
      .slice(0, 5);

    return (
      <div className="rounded-2xl p-4 border dark:border-gray-700">
        <div className="text-sm font-medium mb-3">Recent activity</div>

        <div className="flex flex-col gap-2 text-sm">
          {lastLogins.map((user) => (
            <div key={user.id} className="flex justify-between">
              <span className="truncate">{user.name}</span>
              <span className="text-xs text-zinc-500">
                <DateFormatted date={user.last_login} />
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const FilterPills = ({
    activeFilter,
    onFilter,
    users,
  }: {
    activeFilter: UserFilter;
    onFilter: (filter: UserFilter) => void;
    users: NetbirdUsers;
  }) => {
    const filters = [
      {
        key: 'all' as const,
        label: 'Alles',
        count: users.length,
        color: 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900',
      },
      {
        key: 'active' as const,
        label: 'Actief',
        count: users.filter(
          (user) => user.status === 'active' && !user.pending_approval
        ).length,
        color:
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      },
      {
        key: 'pending' as const,
        label: 'Pending',
        count: users.filter((user) => user.pending_approval).length,
        color:
          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      },
      {
        key: 'blocked' as const,
        label: 'Blocked',
        count: users.filter((user) => user.status === 'blocked').length,
        color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      },
      {
        key: 'inactive' as const,
        label: 'Nooit aangemeld',
        count: users.filter((user) => isNoLoginDate(user.last_login)).length,
        color: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
      },
    ];

    return (
      <div className="flex flex-wrap gap-2 mx-4 mt-2">
        {filters.map((filter) => {
          const isActive = activeFilter === filter.key;

          return (
            <button
              key={filter.key}
              type="button"
              onClick={() => onFilter(filter.key)}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition border
              ${
                isActive
                  ? `${filter.color} border-transparent shadow-sm`
                  : 'border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
              }
            `}
            >
              <span>{filter.label}</span>
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                  isActive
                    ? 'bg-white/20'
                    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500'
                }`}
              >
                {filter.count}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <TextHeader
        title="CMC Zero trust"
        subtitle="Overzicht van de CMC zero trust. Beheer verzoeken"
      />

      <div className="grid grid-cols-6 gap-6 my-8">
        <div className="col-span-4 flex flex-col gap-6">
          <div className="grid grid-cols-3 gap-4 my-8">
            <ExtendedStatCard
              title="Protected identities"
              value={vpnUsers?.length ?? 0}
              subtitle={`${adminUsers} admins / owners`}
              color="green"
              icon={<Icon name="Shield" size="sm" />}
            />

            <ExtendedStatCard
              title="Devices"
              value={vpnPeers?.length ?? 0}
              subtitle={`${serverCount} servers • ${clientCount} clients`}
              color="blue"
              icon={<Icon name="Monitor" size="sm" />}
            />

            <ExtendedStatCard
              title="Pending reviews"
              value={pendingApprovals}
              subtitle={
                pendingApprovals > 0
                  ? 'Approval required'
                  : 'No actions required'
              }
              color={pendingApprovals > 0 ? 'orange' : 'green'}
              icon={<Icon name="Bell" size="sm" />}
            />
          </div>
        </div>

        {/* RIGHT SIDE - FULL HEIGHT */}
        {/* <div className="col-span-2">
          {vpnEvents ? (
            <NetbirdRecentEvents auditEvents={vpnEvents} limit={6} />
          ) : (
            <LoadingSprints />
          )}
        </div> */}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 min-w-0">
          {vpnUsers && vpnPeers ? <Tabs tabs={tabs} /> : <LoadingSprints />}
        </div>
      </div>
    </div>
  );
};
